import Loader from 'reusable/mobx-helpers/Loader'
import { makeAutoObservable } from 'mobx'
import { apiClient } from 'api/trpc/trpc-client'
import { AutocompleteOption } from 'reusable/mui/auto-complete/shared/AutocompleteOption'
import { FormHandlerConfig, formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler'
import { AdvisorClientContactDto } from "@gap/backend/app-services/advisor/client/contact/models/AdvisorClientContactDto"
import { getFullNameFromUser, notNull } from '@gap/utils/utils'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import ProposalsEditStore from '../ProposalsEditStore'
import { srcToFile } from 'reusable/utils/file-operations'
import { Timeouts } from '@gap/utils/date-utils'

const addNewContactOption: AutocompleteOption = {
   id: 'add-new-contact',
   label: '+ Create Contact'
}

interface ProposalsEditRecipientsStoreProps {
   proposalsEditStore: ProposalsEditStore
}

export default class ProposalsEditRecipientsStore {
   loader = new Loader(false)
   logoLoader = new Loader(false)
   clientUserLoader = new Loader(false)
   minimalClientUsers: AdvisorClientContactDto.entity[] = []
   fields: { advisorClientId: string, recipientUserIds: string[] } = {
      advisorClientId: "",
      recipientUserIds: []
   }
   createClientContactModal = false
   createClientContactLoader = new Loader()
   fileUpload: { file: File[], progressPercent: number } = {
      file: [],
      progressPercent: 0
   }
   sp
   uploadedFileInformation: File | null = null

   constructor(sp: ProposalsEditRecipientsStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get formHandler() {
      return formHandler(this.fields, {
         advisorClientId: { type: 'string', onValueChange: this.onClientSelect },
         recipientUserIds: { type: 'any' }
      } satisfies FormHandlerConfig)
   }

   get clients(): AutocompleteOption[] {
      return this.minimalClients.map(client => ({
         id: client.id,
         label: client.name
      }))
   }

   get users(): AutocompleteOption[] {
      const users = this.minimalClientUsers.map(user => ({
         id: user.id,
         label: user.firstName
            ? `${getFullNameFromUser({ firstName: user.firstName, lastName: user.lastName })} (${user.email})`
            : `${user.name} (${user.email})`,
      }))

      users.push(addNewContactOption)

      return users
   }

   get org() {
      return this.sp.proposalsEditStore.org
   }

   get advisorOrgLogoName() {
      // Get everything between /logo/ and the query string after the filename
      return (/\/logo\/([^?]+)/.exec(this.org?.logoUrl || '') || [])[1];
   }

   get proposal() {
      return this.sp.proposalsEditStore.proposal
   }

   get clientName() {
      return this.sp.proposalsEditStore.client?.name
   }

   get minimalClients() {
      return this.sp.proposalsEditStore.sp.cacheStore.clientsCacheStore.clients
   }

   get clientContactOptions() {
      return this.minimalClientUsers.map(user => ({
         id: user.id,
         label: `${user.name} (${user.email})`
      }))
   }

   fetch = async () => {
      await this.loader.executeLoading(async () => {
         if (this.proposal.step1?.logoFileName) {
            const url = await this.getUploadedLogoUrl()
            this.uploadedFileInformation = await srcToFile(url, this.proposal.step1.logoFileName)
            this.fileUpload.file = [this.uploadedFileInformation]
         } else {
            await this.resetLogo()
            await this.useAdvisorLogo()
         }

         if (this.proposal.advisorClientId) {
            this.fields.advisorClientId = this.proposal.advisorClientId
            this.fields.recipientUserIds = (this.proposal.step1?.contacts || []).map(c => c.id)
            await this.onClientSelect(true)
            // This can be loaded while on a different step, only set status if we are on the right step
            if (this.sp.proposalsEditStore.stepperFullscreenStore.activeStepIndex === 0) {
               this.sp.proposalsEditStore.stepperFullscreenStore.markStepStatus(this.fields.recipientUserIds.length >= 1)
            }
         }
      })
   }

   closeCreateClientContactModal = () => {
      this.createClientContactModal = false
   }

   onContactIdsChange = (selectedContactList: string[], advisorContact?: AdvisorClientContactDto.entity) => {
      if (advisorContact && this.minimalClientUsers.find(user => user.id === advisorContact.id) === undefined) {
         this.minimalClientUsers.push(advisorContact)
      }

      if (selectedContactList.find(contact => contact === addNewContactOption.id)) {
         this.createClientContactModal = true
      } else {
         this.fields.recipientUserIds.splice(0, this.fields.recipientUserIds.length, ...selectedContactList)
      }
      if (!this.fields.recipientUserIds.length) {
         this.sp.proposalsEditStore.proposal.editingStepIndex = this.sp.proposalsEditStore.stepperFullscreenStore.activeStepIndex
      }
      this.sp.proposalsEditStore.stepperFullscreenStore.markStepStatus(this.fields.recipientUserIds.length >= 1)
      this.updateProposal()
   }

   onClientSelect = async (init = false) => {
      if (!this.fields.advisorClientId) {
         // Because of the form handler, this is set to null
         this.fields.advisorClientId = ''
         this.sp.proposalsEditStore.proposal.editingStepIndex = this.sp.proposalsEditStore.stepperFullscreenStore.activeStepIndex
         await this.updateProposal()
         return
      }
      if (this.fields.advisorClientId !== this.proposal.advisorClientId && init === false) {
         // Reset the recipient user ids when the client changes
         this.fields.recipientUserIds = []
      }
      await this.clientUserLoader.executeLoading(async () => {
         this.sp.proposalsEditStore.stepperFullscreenStore.markStepStatus(this.fields.recipientUserIds.length >= 1)

         const [_, { org: clientOrg }, minimalClientUsers] = await Promise.all([
            init === false ? this.updateProposal() : Promise.resolve(),
            apiClient.advisor.ensureClientOrgExists.mutate({ advisorClientId: notNull(this.fields.advisorClientId) }),
            apiClient.advisor.getClientContacts.query({ clientId: this.fields.advisorClientId, includeHiddenStatus: true, includeArchivedStatus: true })
         ])
         this.sp.proposalsEditStore.sp.proposalsViewStore.clientOrg = clientOrg

         // remove all users with status archived unless they are a recipient in the proposal
         const statusToInclude = clientOrg.xeroAuthorised || clientOrg.xeroDisconnectedViaXPM ? 'active' : 'active,hidden'
         this.minimalClientUsers = minimalClientUsers.filter(user => statusToInclude.includes(user.status) || this.fields.recipientUserIds.includes(user.id))
      })
   }

   getUploadUrl = async (file: File) => {
      this.uploadedFileInformation = file
      const { url } = await apiClient.proposals.getProposalsLogoUploadUrl.query({ orgId: notNull(this.org?.orgId), fileName: file.name, proposalId: this.proposal.id })
      return url
   }

   resetLogo = async () => {
      this.uploadedFileInformation = null
      this.fileUpload.file.splice(0, this.fileUpload.file.length)
      this.proposal.step1 = this.proposal.step1 || {}
      this.proposal.step1.logoFileName = undefined
      await this.sp.proposalsEditStore.autoSave.updateProposal()
   }

   useAdvisorLogo = async () => {
      if (!this.advisorOrgLogoName) {
         return
      }
      await this.logoLoader.executeLoading(async () => {
         this.uploadedFileInformation = null
         const orgLogoUrl = notNull(this.org?.logoUrl)
         // Download the current logo
         const orgLogoData = await (await fetch(orgLogoUrl)).blob()
         const orgLogoFile = new File([orgLogoData], this.advisorOrgLogoName)
         const uploadUrl = await this.getUploadUrl(orgLogoFile)
         // Upload the org logo to the proposal logo upload URL
         await fetch(uploadUrl, {
            method: 'PUT',
            body: orgLogoFile,
         })
         this.proposal.step1 = this.proposal.step1 || {}
         this.proposal.step1.logoFileName = orgLogoFile.name
         await this.sp.proposalsEditStore.updateProposal()
         this.uploadedFileInformation = orgLogoFile
         this.fileUpload.file = [orgLogoFile]
      })
   }

   getUploadedLogoUrl = async () => {
      if (this.uploadedFileInformation) {
         const logo = await apiClient.proposals.getProposalsLogo.query({ orgId: notNull(this.org?.orgId), fileName: notNull(this.uploadedFileInformation.name), proposalId: this.proposal.id })

         if (!logo || !logo.url || logo.url === '') {
            // For some weird reason, it returns '' for a split second right after upload. Give it a second and try again
            await new Promise(f => setTimeout(f, 1 * Timeouts.second))
            const logoAttempt2 = await apiClient.proposals.getProposalsLogo.query({ orgId: notNull(this.org?.orgId), fileName: notNull(this.uploadedFileInformation.name), proposalId: this.proposal.id })
            return logoAttempt2.url
         }

         return logo.url
      } else {
         const advisorOrg = notNull(this.org)
         if (advisorOrg.logoUrl) {
            return advisorOrg.logoUrl
         }
      }

      return ''
   }

   onNewLogo = async () => {
      await this.logoLoader.executeLoading(async () => {
         await this.updateProposal()
      })
   }

   updateProposal = async () => {
      if (!this.proposal.step1) {
         this.proposal.step1 = {}
      }

      this.proposal.advisorClientId = this.fields.advisorClientId

      this.proposal.step1.clientName = this.clientName
      this.proposal.step1.logoFileName = this.uploadedFileInformation?.name
      this.proposal.step1.contacts = this.fields.recipientUserIds.map(userId => {
         const contact = notNull(this.minimalClientUsers.find(clientUser => clientUser.id === userId))
         return {
            email: contact.email,
            id: userId,
            name: contact.name
         }
      })
      await this.sp.proposalsEditStore.autoSave.updateProposal()
   }
}

export const { Provider: ProposalsEditRecipientsStoreProvider, useProvidedStore: useProposalsEditRecipientsStore } = createStoreContext(ProposalsEditRecipientsStore)
